import { useEffect, useRef, useState } from "react";
import { Users } from "lucide-react";
import { getCampaignStats } from "../lib/api";

const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const useCountUp = (target, duration = 1200) => {
  const [value, setValue] = useState(0);
  const rafRef = useRef();
  const startRef = useRef();
  const fromRef = useRef(0);

  useEffect(() => {
    fromRef.current = value;
    startRef.current = null;
    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const p = Math.min(1, (ts - startRef.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = Math.round(fromRef.current + (target - fromRef.current) * eased);
      setValue(next);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return value;
};

export default function ImpactCounter({ slug = "flood_relief", refreshKey = 0 }) {
  const [stats, setStats] = useState({
    total_raised_inr: 0,
    goal_inr: 500000,
    donor_count: 0,
    percent: 0,
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getCampaignStats(slug);
        if (!cancelled) {
          setStats(data);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [slug, refreshKey]);

  const raised = useCountUp(loaded ? stats.total_raised_inr : 0);
  const donors = useCountUp(loaded ? stats.donor_count : 0);
  const percent = Math.min(100, Math.round((raised / stats.goal_inr) * 100)) || 0;

  return (
    <div
      className="bg-white rounded-2xl border border-bone-soft px-5 py-4 lg:px-6 lg:py-5 shadow-[0_15px_40px_rgba(30,59,39,0.10)]"
      data-testid="impact-counter"
    >
      <div className="flex items-end justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-terracotta opacity-70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-terracotta" />
            </span>
            <span className="text-[11px] uppercase tracking-[0.14em] text-ink/60 font-semibold">
              Raised so far
            </span>
          </div>
          <div
            className="text-3xl lg:text-4xl text-forest tracking-tight tabular-nums"
            style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 900 }}
            data-testid="impact-counter-raised"
          >
            {formatINR(raised)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.14em] text-ink/60 font-semibold mb-0.5">
            Goal
          </div>
          <div className="text-lg lg:text-xl font-serif text-ink/80 tabular-nums" data-testid="impact-counter-goal">
            {formatINR(stats.goal_inr)}
          </div>
        </div>
      </div>

      <div className="relative h-2.5 w-full bg-bone-soft/70 rounded-full overflow-hidden" data-testid="impact-counter-progress">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-terracotta to-[#d97a5d] rounded-full transition-[width] duration-700 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>

      <div className="flex items-center justify-between mt-3 text-sm">
        <div className="flex items-center gap-1.5 text-ink/70" data-testid="impact-counter-donors">
          <Users className="w-4 h-4 text-forest" />
          <span className="tabular-nums font-semibold text-forest">{donors.toLocaleString("en-IN")}</span>
          <span>kind souls have chipped in</span>
        </div>
        <div className="text-forest font-semibold tabular-nums" data-testid="impact-counter-percent">
          {percent}%
        </div>
      </div>
    </div>
  );
}
